import React, { useState } from "react";
import { useDispatch, useStore } from "react-redux";
import CommunicationService from "./services/CommunicationService"; // adjust path if needed
import styles from "./QueryBuilder.module.css";

const COMMON_PARAMS = [
    { value: "patient", label: "patient (reference)" },
    { value: "subject", label: "subject (reference)" },
    { value: "sender", label: "sender (reference)" }, // provider
    { value: "category", label: "category" },
    { value: "received", label: "received (date range)" },
    { value: "sent", label: "sent (date range)" },
];

// Category options for dropdown
const CATEGORY_OPTIONS = ["Alert", "Notification", "Reminder", "Instruction"];

export default function QueryBuilder({ fhirClient, contextProvider, onClose, setSelectedComm }) {
    const dispatch = useDispatch();
    const store = useStore();
    const [loading, setLoading] = useState(false);
    const [params, setParams] = useState([]);
    const [error, setError] = useState(null);

    // Search state for active param input (patient or provider)
    const [searchTerm, setSearchTerm] = useState("");
    const [searchedResources, setSearchedResources] = useState([]);
    const [activeParamIndex, setActiveParamIndex] = useState(null);
    const [activeParamField, setActiveParamField] = useState(null); // "patient" or "sender"

    // Separate display name maps keyed by param index
    const [patientDisplayNames, setPatientDisplayNames] = useState({});
    const [providerDisplayNames, setProviderDisplayNames] = useState({});

    const communicationService = React.useMemo(() => {
        if (!fhirClient) return null;
        return new CommunicationService(fhirClient);
    }, [fhirClient]);

    const addParam = () => {
        setParams([...params, { field: "", value: "", startDate: "", endDate: "" }]);
    };

    const updateParam = (index, key, val) => {
        const updated = [...params];
        updated[index][key] = val;
        setParams(updated);

        if (key === "field") {
            // If changing from patient or sender, reset relevant state and display names
            if (val !== "patient" && patientDisplayNames[index]) {
                setPatientDisplayNames((prev) => {
                    const copy = { ...prev };
                    delete copy[index];
                    return copy;
                });
            }
            if (val !== "sender" && providerDisplayNames[index]) {
                setProviderDisplayNames((prev) => {
                    const copy = { ...prev };
                    delete copy[index];
                    return copy;
                });
            }

            // Reset search UI if param field changed
            if (index === activeParamIndex) {
                setActiveParamIndex(null);
                setSearchTerm("");
                setSearchedResources([]);
                setActiveParamField(null);
            }
        }
    };

    const removeParam = (index) => {
        const updated = [...params];
        updated.splice(index, 1);
        setParams(updated);

        if (index === activeParamIndex) {
            setActiveParamIndex(null);
            setSearchTerm("");
            setSearchedResources([]);
            setActiveParamField(null);
        }

        setPatientDisplayNames((prev) => {
            const copy = { ...prev };
            delete copy[index];
            return copy;
        });
        setProviderDisplayNames((prev) => {
            const copy = { ...prev };
            delete copy[index];
            return copy;
        });
    };

    // Search either Patients or Practitioners by name
    const handleSearchByName = async () => {
        if (!searchTerm.trim()) {
            setError(`Please enter a ${activeParamField === "patient" ? "patient" : "provider"} name.`);
            setSearchedResources([]);
            return;
        }
        if (!fhirClient) {
            setError("FHIR Client is not initialized.");
            setSearchedResources([]);
            return;
        }

        try {
            let resourceType = "Patient";
            if (activeParamField === "sender") resourceType = "Practitioner";

            const searchResults = await fhirClient.request(`${resourceType}?name=${encodeURIComponent(searchTerm)}`);
            const found = searchResults.entry?.map((e) => e.resource) || [];
            setSearchedResources(found);
            setError(found.length ? null : `No ${resourceType.toLowerCase()}s found.`);
        } catch (err) {
            console.error(`Error searching ${activeParamField}:`, err);
            setError(`Error searching ${activeParamField}.`);
            setSearchedResources([]);
        }
    };

    // Handle selection of Patient or Provider from search results
    const handleSelectResource = (resource, index) => {
        let refPrefix = "Patient";
        let displayName = "";

        if (activeParamField === "sender") {
            refPrefix = "Practitioner";
            displayName = `${resource.name?.[0]?.given?.join(" ") || ""} ${resource.name?.[0]?.family || ""}`.trim();
        } else if (activeParamField === "patient") {
            displayName = `${resource.name?.[0]?.given?.join(" ") || ""} ${resource.name?.[0]?.family || ""}`.trim();
        }

        const resourceRef = `${refPrefix}/${resource.id}`;

        // Update param value with reference string
        const updated = [...params];
        updated[index].value = resourceRef;
        setParams(updated);

        // Store display name in proper map
        if (activeParamField === "patient") {
            setPatientDisplayNames((prev) => ({
                ...prev,
                [index]: displayName,
            }));
        } else if (activeParamField === "sender") {
            setProviderDisplayNames((prev) => ({
                ...prev,
                [index]: displayName,
            }));
        }

        // Clear search UI and errors
        setSearchTerm("");
        setSearchedResources([]);
        setActiveParamIndex(null);
        setActiveParamField(null);
        setError(null);
    };

    // Build query string from params plus recipient from contextProvider (hidden from UI)
    const buildSearchParams = () => {
        const recipientRef = contextProvider ? contextProvider.reference || (contextProvider.id ? `Practitioner/${contextProvider.id}` : "") : "";
        const paramStrings = [];

        // Check if any param is 'sent' (outbound search)
        const isOutbound = params.some(p => p.field === "sent");

        params.forEach((p) => {
            if (!p.field) return;

            if (p.field === "received" || p.field === "sent") {
                if (p.startDate) {
                    paramStrings.push(`${p.field}=ge${encodeURIComponent(p.startDate)}`);
                }
                if (p.endDate) {
                    paramStrings.push(`${p.field}=le${encodeURIComponent(p.endDate)}`);
                }
            } else {
                if (p.value) {
                    paramStrings.push(`${encodeURIComponent(p.field)}=${encodeURIComponent(p.value)}`);
                }
            }
        });
        const paramString = paramStrings.join("&");

        // Only include recipient param if not outbound (not searching by 'sent')
        if (!isOutbound && recipientRef) {
            return `recipient=${encodeURIComponent(recipientRef)}${paramString ? "&" + paramString : ""}`;
        }
        return paramString;
    };

    const runQuery = async () => {
        if (!communicationService) return;
        setLoading(true);
        setError(null);

        try {
            const searchParams = buildSearchParams();

            // If any param is 'sent', set direction to 'sent', otherwise use 'received'
            const hasSentParam = params.some(p => p.field === "sent");
            const direction = hasSentParam ? "sent" : "received";

            const results = await communicationService.getCommunicationsPage(
                contextProvider?.id,
                10,
                null,
                direction, // <-- Use direction based on param
                dispatch,
                store.getState,
                searchParams,
                false  
            );

            if (!results.data || results.total === 0) {
                setError("No results found. Try adjusting your search criteria.");
            } else {
                setError(null);
                setSelectedComm(null);
                if (onClose) onClose();
            }
        } catch (err) {
            console.error(err);
            setError("An error occurred while fetching results.");
        }
        setLoading(false);
    };

    return (
        <div className={styles.eastSection}>
            <h2 className={styles.title}>Add search criteria...</h2>

            {error && <div className={styles.errorMessage}>{error}</div>}

            {params.map((p, idx) => (
                <div key={idx} className={styles.paramRow} style={{ position: "relative" }}>
                    <select
                        className={styles.selectInput}
                        value={p.field}
                        onChange={(e) => {
                            const val = e.target.value;
                            updateParam(idx, "field", val);
                            if (val === "patient" || val === "sender") {
                                setActiveParamIndex(idx);
                                setActiveParamField(val);
                                if (val === "patient") {
                                    setSearchTerm(patientDisplayNames[idx] || "");
                                } else {
                                    setSearchTerm(providerDisplayNames[idx] || "");
                                }
                                setSearchedResources([]);
                                setError(null);
                            } else if (idx === activeParamIndex) {
                                setActiveParamIndex(null);
                                setActiveParamField(null);
                                setSearchTerm("");
                                setSearchedResources([]);
                            }
                        }}
                    >
                        <option value="">Select parameter</option>
                        {COMMON_PARAMS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>

                    {/* Field input rendering */}
                    {p.field === "received" || p.field === "sent" ? (
                        <>
                            <input
                                type="date"
                                className={styles.dateInput}
                                placeholder="Start date"
                                value={p.startDate}
                                onChange={(e) => updateParam(idx, "startDate", e.target.value)}
                            />
                            <input
                                type="date"
                                className={styles.dateInput}
                                placeholder="End date"
                                value={p.endDate}
                                onChange={(e) => updateParam(idx, "endDate", e.target.value)}
                            />
                        </>
                    ) : p.field === "patient" || p.field === "sender" ? (
                        idx === activeParamIndex ? (
                            <>
                                <input
                                    type="text"
                                    className={styles.textInput}
                                    placeholder={`Find a ${p.field === "patient" ? "Patient" : "Provider"}`}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleSearchByName();
                                        }
                                    }}
                                />
                                {searchedResources.length > 0 && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            backgroundColor: "#f8f9fa",
                                            border: "1px solid #ced4da",
                                            boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
                                            maxHeight: "250px",
                                            overflowY: "auto",
                                            width: "calc(100% + 150px)",
                                            top: "100%",
                                            left: 0,
                                            zIndex: 1000,
                                        }}
                                    >
                                        {searchedResources.map((resource) => (
                                            <div
                                                key={resource.id}
                                                style={{ padding: "8px", cursor: "pointer" }}
                                                onClick={() => handleSelectResource(resource, idx)}
                                            >
                                                <div>
                                                    <strong>
                                                        {resource.name?.[0]?.given?.join(" ")} {resource.name?.[0]?.family}
                                                    </strong>
                                                </div>
                                                <div className="text-muted small">
                                                    {resource.gender
                                                        ? resource.gender.charAt(0).toUpperCase() + resource.gender.slice(1)
                                                        : "Unknown"}
                                                </div>
                                                <div className="text-muted small">
                                                    {resource.address?.[0]?.city || "N/A"}, {resource.address?.[0]?.state || "N/A"}
                                                </div>
                                                {p.field === "patient" && (
                                                    <div className="text-muted small">Birth Date: {resource.birthDate || "N/A"}</div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        ) : (
                            <input
                                type="text"
                                className={styles.textInput}
                                placeholder="Value"
                                value={p.field === "patient" ? patientDisplayNames[idx] || "" : providerDisplayNames[idx] || ""}
                                readOnly
                                onFocus={() => {
                                    setActiveParamIndex(idx);
                                    setActiveParamField(p.field);
                                    setSearchTerm(p.field === "patient" ? patientDisplayNames[idx] || "" : providerDisplayNames[idx] || "");
                                    setSearchedResources([]);
                                }}
                                style={{ cursor: "pointer", backgroundColor: "#e9ecef" }}
                            />
                        )
                    ) : p.field === "category" ? (
                        <select
                            className={styles.selectInput}
                            value={p.value}
                            onChange={(e) => updateParam(idx, "value", e.target.value)}
                        >
                            <option value="">Select Category</option>
                            {CATEGORY_OPTIONS.map((cat) => (
                                <option key={cat} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <input
                            type="text"
                            className={styles.textInput}
                            placeholder="Value"
                            value={p.value}
                            onChange={(e) => updateParam(idx, "value", e.target.value)}
                        />
                    )}

                    <button className={styles.removeButton} onClick={() => removeParam(idx)}>
                        X
                    </button>
                </div>
            ))}

            <div className={styles.actions}>
                <button className={styles.actionButton} onClick={addParam}>
                    Add Parameter
                </button>
                <button className={styles.actionButton} onClick={runQuery} disabled={loading}>
                    {loading ? "Searching..." : "Run Query"}
                </button>
            </div>
        </div>
    );
}
